import React from 'react';
import PropTypes from 'prop-types';
import Grid from '@material-ui/core/Grid';
import Container from '@material-ui/core/Container';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Page from '../../../components/Page';
import Menu from '../components/Menu';
import { hasPermission } from '../../../lib/permissions';
import BreadcrumbHeader from '../components/BreadcrumbHeader';
import EditProfile from '../components/EditProfile';
import ListingsSummary from '../components/ListingsSummary';
import EditListing from '../components/EditListing';
import PremisesSummary from '../components/PremisesSummary';
import EditPremises from '../components/EditPremises';
import ReviewsSummary from '../components/ReviewsSummary';
import ReviewResponse from '../components/ReviewResponse';
import AccountSettings from '../components/AccountSettings';
import ProviderResources from '../components/ProviderResources';
import UserManager from '../components/UserManager';
import Dashboard from '../components/Dashboard';
import BillingSettings from '../components/BillingSettings';
import { useHistory } from 'react-router-dom';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import NotOnMobilePage from '../../pages/NotOnMobilePage';
import AccreditedOnly from '../components/AccreditedOnly';
import MatchingFees from '../components/MatchingFees';
import { useSelector, useDispatch } from 'react-redux';
import { AuthStore } from '../../../store';
import pages from '../components/pages';

const pageTitles = {
  'profile': 'Manage Organisation Profile',
  'listings': 'Manage Listings',
  'newListing': 'Create New Listing',
  'premises': 'Manage Premises',
  'newPremises': 'Create New Premises',
  'dashboard': 'Engagement Dashboard',
  'users': 'Manage Users and Access',
  'reviews': 'View and Respond to Reviews',
  'settings': 'Account Settings',
  'resources': 'Resources',
  'billing': 'Billing',
  'matchingfees': 'Matching Fees',
};

const useStyles = makeStyles(theme => ({
  menu: {
    width: '100%',
    display: 'flex',
    flexWrap: 'wrap',
    alignContent: 'flex-start',
    justifyContent: 'flex-start',
    padding: '2rem 0 2rem 1rem',
    borderRight: `1px solid ${theme.palette.common.silver}`,
  },
  content: {
    width: '100%',
    display: 'flex',
    flexWrap: 'wrap',
    alignContent: 'flex-start',
    justifyContent: 'flex-start',
    padding: '2rem 2rem',
  },
  alert: {
    width: '100%',
    padding: '1rem 0',
  }
}));

const providerRoles = (profile, providerUuid) => {
  // return a list of roles held by this <profile> for this provider.  May be empty [].
  const roles = profile && profile.roles && profile.roles.find(r => r.provider_uuid === providerUuid);
  return roles || [];
};

const AdminPage = ({location, match}) => {
  const history = useHistory();
  const classes = useStyles();
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('sm'));
  const {uuid, page} = match.params;
  const urlParams = new URLSearchParams(location.search);
  const item = urlParams.get('item');
  const nonAdminUrl = `/provider/${uuid}`;
  const basePath = `/provider/${uuid}/admin`;
  const [accessGranted, setAccessGranted] = React.useState(false);
  const title = pageTitles[page];
  const profile = useSelector(state => state.auth.profile);
  const dispatch = useDispatch();
  const role = providerRoles(profile, uuid);

  const hasPagePermission = (page) => hasPermission(profile, uuid, page.permission);

  const defaultPage = () => {
    const page = Object.values(pages).find(page => hasPagePermission(page));
    return page && page.path;
  };

  const selectPage = value => {
    history.push({ pathname: `${basePath}/${value}` });
  };

  const selectItem = value => {
    history.push({ pathname: `${basePath}/${page}`, search: '?item='+encodeURI(value)});
  };

  const back = () => {
    history.push({ pathname: `${basePath}/${page}` });
  };

  const verifyAccess = async () => {
    let allowed = false;
    try {
      if (!profile) {
        history.replace(nonAdminUrl);
      } else {
        const allowedProviders = profile.roles.map(r => r.provider_uuid);
        allowed = profile.is_site_admin || allowedProviders.includes(uuid);
        setAccessGranted(allowed);
      }
    } catch (err) {
      console.error(err); // eslint-disable-line no-console
    }
    if (!allowed) history.replace(nonAdminUrl);
  };

  // if the user does not have access to the current page, find a page they do
  // have access to.  Everyone can access the 'resources' page, so it will always
  // find something.
  React.useEffect(() => {
    if (!hasPagePermission(pages[page])) {
      selectPage(defaultPage());
    }
  }, [page, role]);  // eslint-disable-line

  React.useEffect(() => {
    verifyAccess();
  }, [profile]); // eslint-disable-line

  React.useEffect(() => {
    dispatch(AuthStore.refreshProfile());
  }, []);  // eslint-disable-line

  if (!accessGranted) {
    return null;
  }

  if (mobile) {
    return (<NotOnMobilePage message="The provider admin console is not available for mobile devices right now. Please use a Desktop or Laptop PC to use this feature. You can still explore the rest of the site on your mobile device." />);
  }

  window.scrollTo(0,0);

  return (
    <Page title={`${title} - Clickability`}>
      <Container maxWidth="lg" fixed disableGutters={true}>
        <Grid container spacing={0}>
          <Grid item md={2} className={classes.menu}>
            <Typography variant="h6" color="primary">Provider Admin</Typography>
            <Menu
              permissions={role.permissions}
              onClick={selectPage}
              isSiteAdmin={profile && profile.is_site_admin}
            />
          </Grid>
          <Grid item md={10} className={classes.content}>
            <BreadcrumbHeader
              title={title}
              item={item}
              back={back}
            />
            {page === 'profile' ? (
              <EditProfile
                providerUuid={uuid}
              />
            ) : null}
            {page === 'listings' && item === null ? (
              <ListingsSummary
                providerUuid={uuid}
                selectListing={selectItem}
                newListing={() => selectPage('newListing')}
              />
            ) : null}
            {page === 'listings' && item !== null ? (
              <EditListing
                providerUuid={uuid}
                listingUuid={item}
                back={() => selectPage('listings')}
              />
            ) : null}
            {page === 'newListing' ? (
              <EditListing
                providerUuid={uuid}
                listingUuid={null}
                back={() => selectPage('listings')}
              />
            ) : null}
            {page === 'premises' && item === null ? (
              <PremisesSummary
                providerUuid={uuid}
                selectPremises={selectItem}
                newPremises={() => selectPage('newPremises')}
              />
            ) : null}
            {page === 'premises' && item !== null ? (
              <EditPremises
                providerUuid={uuid}
                premiseUuid={item}
                back={() => selectPage('premises')}
              />
            ) : null}
            {page === 'newPremises' ? (
              <EditPremises
                providerUuid={uuid}
                premiseUuid={null}
                back={() => selectPage('premises')}
              />
            ) : null}
            {page === 'dashboard' ? (
              <AccreditedOnly providerUuid={uuid}>
                <Dashboard providerUuid={uuid} />
              </AccreditedOnly>
            ) : null}
            {page === 'reviews' && item === null ? (
              <ReviewsSummary
                providerUuid={uuid}
                selectReview={selectItem}
              />
            ) : null}
            {page === 'reviews' && item !== null ? (
              <ReviewResponse
                reviewUuid={item}
                providerUuid={uuid}
                back={() => selectPage('reviews')}
              />
            ) : null}
            {page === 'settings' ? (
              <AccountSettings
                providerUuid={uuid}
              />
            ) : null}
            {page === 'users' ? (
              <UserManager
                providerUuid={uuid}
              />
            ) : null}
            {page === 'resources' ? (
              <ProviderResources
              />
            ) : null}
            {page === 'billing' ? (
              <BillingSettings
                providerUuid={uuid}
              />
            ) : null}
            {page === 'matchingfees' ? (
              <MatchingFees
                providerUuid={uuid}
              />
            ) : null}
          </Grid>
        </Grid>
      </Container>
    </Page>
  );

};

AdminPage.propTypes = {
  match: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
};

export default AdminPage;
